// frontend/src/components/UI/StackedCard.js
import React, { useState } from 'react';
import { Box, Paper, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';

const CardStack = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: 360,
  cursor: 'pointer',
  perspective: '1200px',
}));

const CardLayer = styled(Paper)(({ theme, index, isHovered }) => {
  const baseOffset = index * 8;
  const raiseAmount = [10, 16, 22];

  const translateY = isHovered
    ? baseOffset - raiseAmount[index]
    : baseOffset;
  const translateX = isHovered
    ? baseOffset + index * 3
    : baseOffset;
  const rotate = isHovered ? index * 0.3 : 0;

  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius: theme.spacing(3),
    backgroundColor: index === 2
      ? alpha('#ffffff', 0.94)
      : index === 1
        ? alpha('#ffffff', 0.82)
        : alpha('#ffffff', 0.68),
    boxShadow: index === 2
      ? '0 8px 24px rgba(25, 40, 45, 0.10)'
      : index === 1
        ? '0 5px 16px rgba(25, 40, 45, 0.055)'
        : '0 3px 10px rgba(25, 40, 45, 0.035)',
    border: `1px solid ${index === 2
      ? alpha('#ffffff', 0.90)
      : alpha('#ffffff', 0.68)}`,
    zIndex: index,
    transform: `translate3d(${translateX}px, ${translateY}px, 0) rotate(${rotate}deg)`,
    transition: 'transform 520ms cubic-bezier(0.22, 0.8, 0.24, 1)',
    transformStyle: 'preserve-3d',
    willChange: 'transform',
    transitionDelay: isHovered
      ? `${index * 70}ms`
      : `${(2 - index) * 90}ms`,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      border: index < 2 ? `1px solid ${alpha('#ffffff', 0.62)}` : 'none',
      pointerEvents: 'none',
    },
  };
});

const ContentContainer = styled(Box)(({ theme, gradientColor }) => ({
  position: 'relative',
  zIndex: 10,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(3),
  overflow: 'hidden',

  // Свежий "soft glass / editorial" градиент:
  // цвет научной области становится частью поверхности карточки,
  // но не превращает её в однотонную заливку.
  background: gradientColor
    ? `
      radial-gradient(
        circle at 88% 10%,
        ${alpha(gradientColor, 0.34)} 0%,
        ${alpha(gradientColor, 0.18)} 22%,
        transparent 46%
      ),
      radial-gradient(
        circle at 8% 92%,
        ${alpha(gradientColor, 0.22)} 0%,
        transparent 38%
      ),
      linear-gradient(
        145deg,
        #ffffff 0%,
        ${alpha(gradientColor, 0.06)} 25%,
        ${alpha(gradientColor, 0.15)} 52%,
        ${alpha(gradientColor, 0.34)} 78%,
        ${gradientColor} 100%
      )
    `
    : `
      linear-gradient(
        145deg,
        #ffffff 0%,
        #f7f9f9 100%
      )
    `,

  // Лёгкая внутренняя рамка помогает цветной поверхности
  // отделиться от белых слоёв стопки.
  border: gradientColor
    ? `1px solid ${alpha(gradientColor, 0.24)}`
    : '1px solid rgba(0,0,0,0.04)',

  boxShadow: gradientColor
    ? `inset 0 1px 0 rgba(255,255,255,0.85),
       inset 0 -24px 48px ${alpha(gradientColor, 0.08)}`
    : 'inset 0 1px 0 rgba(255,255,255,0.9)',

  // Небольшой световой блик сверху.
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '8%',
    right: '8%',
    height: '42%',
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0) 100%)',
    opacity: 0.9,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-35%',
    right: '-18%',
    width: '55%',
    height: '70%',
    pointerEvents: 'none',
    borderRadius: '50%',
    background: gradientColor
      ? `radial-gradient(circle, ${alpha(gradientColor, 0.16)} 0%, transparent 68%)`
      : 'none',
    filter: 'blur(18px)',
  },
}));


const GeometricDecor = styled(Box)(({ gradientColor }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  overflow: 'hidden',
  pointerEvents: 'none',

  '&::before': {
    content: '""',
    position: 'absolute',
    width: '190px',
    height: '190px',
    top: '-72px',
    right: '-42px',
    borderRadius: '50%',
    background: gradientColor
      ? `radial-gradient(circle, ${alpha(gradientColor, 0.34)} 0%, ${alpha(gradientColor, 0.18)} 42%, transparent 72%)`
      : 'none',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    width: '155px',
    height: '155px',
    bottom: '-78px',
    left: '-48px',
    borderRadius: '38% 62% 58% 42%',
    background: gradientColor ? alpha(gradientColor, 0.25) : 'transparent',
    transform: 'rotate(-24deg)',
  },
}));

const GeometricShape = styled(Box)(({ gradientColor, variant }) => {
  const shapes = {
    circle: {
      width: 92, height: 92, top: 72, right: -28,
      borderRadius: '50%', transform: 'rotate(12deg)',
    },
    diamond: {
      width: 76, height: 76, top: 118, right: 34,
      borderRadius: 18, transform: 'rotate(45deg)',
    },
    ring: {
      width: 116, height: 116, bottom: 36, right: -42,
      borderRadius: '50%',
      border: `18px solid ${gradientColor ? alpha(gradientColor, 0.24) : 'transparent'}`,
      background: 'transparent',
      transform: 'rotate(-14deg)',
    },
  };

  const shape = shapes[variant] || shapes.circle;

  return {
    position: 'absolute',
    ...shape,
    background: shape.background || (gradientColor ? alpha(gradientColor, 0.24) : 'transparent'),
    opacity: 0.95,
    mixBlendMode: 'multiply',
    pointerEvents: 'none',
  };
});

const CardTop = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.8, 2.25, 1.25),
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const WhiteDivider = styled(Box)(({ theme }) => ({
  height: 1,
  flexShrink: 0,
  background: `linear-gradient(90deg, transparent, ${alpha('#ffffff', 0.95)} 12%, ${alpha('#ffffff', 0.95)} 88%, transparent)`,
  boxShadow: `0 0 5px ${alpha('#ffffff', 0.55)}`,
}));

const CardBottom = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 2.25, 1.35),
  marginTop: 'auto',
  flexShrink: 0,
  width: '100%',
  minHeight: 58,
  '& .MuiTypography-root': {
    color: alpha('#000', 0.78),
  },
  '& .MuiSvgIcon-root': {
    color: alpha('#000', 0.58),
  },
}));

const EmptyCard = styled(Box)({
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
});

const StackedCard = ({ children, gradientColor, onClick, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);

  const topContent = children?.top || children;
  const bottomContent = children?.bottom;

  return (
    <CardStack
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      {...props}
    >
      <CardLayer index={0} isHovered={isHovered} elevation={0}>
        <EmptyCard />
      </CardLayer>

      <CardLayer index={1} isHovered={isHovered} elevation={0}>
        <EmptyCard />
      </CardLayer>

      <CardLayer index={2} isHovered={isHovered} elevation={0}>
        <ContentContainer gradientColor={gradientColor}>
          <GeometricDecor gradientColor={gradientColor}>
            <GeometricShape gradientColor={gradientColor} variant="circle" />
            <GeometricShape gradientColor={gradientColor} variant="diamond" />
            <GeometricShape gradientColor={gradientColor} variant="ring" />
          </GeometricDecor>

          <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardTop>{topContent}</CardTop>
          {bottomContent && (
            <>
              <WhiteDivider />
              <CardBottom>{bottomContent}</CardBottom>
            </>
          )}
          </Box>
        </ContentContainer>
      </CardLayer>
    </CardStack>
  );
};

export default StackedCard;
